import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SelectButtonModule } from 'primeng/selectbutton';
import { LayoutService } from '../service/layout.service';
import { ThemeApplyService } from '../service/theme-apply.service';
import { THEME_PRESET_NAMES, THEME_SURFACES } from '../service/theme.constants';
import { ThemePreferenceService } from '../../services/theme-preference.service';
import { IThemeSettings } from '../../models/theme-preference';

@Component({
    selector: 'app-configurator',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectButtonModule],
    template: `
        <div class="flex flex-col gap-4">
            <div>
                <span class="text-sm text-muted-color font-semibold">Primary</span>
                <div class="pt-2 flex gap-2 flex-wrap justify-start">
                    @for (primaryColor of primaryColors(); track primaryColor.name) {
                        <button
                            type="button"
                            [title]="primaryColor.name"
                            (click)="updateColors($event, 'primary', primaryColor.name)"
                            [ngClass]="{ 'outline-primary': primaryColor.name === selectedPrimaryColor() }"
                            class="border-none w-5 h-5 rounded-full p-0 cursor-pointer outline-none outline-offset-1"
                            [style]="{
                                'background-color': primaryColor?.name === 'noir' ? 'var(--text-color)' : primaryColor?.palette?.['500']
                            }"
                        ></button>
                    }
                </div>
            </div>
            <div>
                <span class="text-sm text-muted-color font-semibold">Surface</span>
                <div class="pt-2 flex gap-2 flex-wrap justify-start">
                    @for (surface of surfaces; track surface.name) {
                        <button
                            type="button"
                            [title]="surface.name"
                            (click)="updateColors($event, 'surface', surface.name)"
                            [ngClass]="{ 'outline-primary': selectedSurfaceColor() ? selectedSurfaceColor() === surface.name : layoutService.layoutConfig().darkTheme ? surface.name === 'zinc' : surface.name === 'slate' }"
                            class="border-none w-5 h-5 rounded-full p-0 cursor-pointer outline-none outline-offset-1"
                            [style]="{
                                'background-color': surface?.name === 'noir' ? 'var(--text-color)' : surface?.palette?.['500']
                            }"
                        ></button>
                    }
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <span class="text-sm text-muted-color font-semibold">Presets</span>
                <p-selectbutton [options]="presets" [ngModel]="selectedPreset()" (ngModelChange)="onPresetChange($event)" [allowEmpty]="false" size="small" />
            </div>
            <div *ngIf="showMenuModeButton()" class="flex flex-col gap-2">
                <span class="text-sm text-muted-color font-semibold">Menu Mode</span>
                <p-selectbutton [ngModel]="menuMode()" (ngModelChange)="onMenuModeChange($event)" [options]="menuModeOptions" [allowEmpty]="false" size="small" />
            </div>
            <button type="button" class="text-sm text-muted-color underline text-left p-0 border-none bg-transparent cursor-pointer" (click)="resetTheme()">
                Reset to defaults
            </button>
        </div>
    `,
    host: {
        class: 'hidden absolute top-[3.25rem] right-0 w-72 p-4 bg-surface-0 dark:bg-surface-900 border border-surface rounded-border origin-top shadow-[0px_3px_5px_rgba(0,0,0,0.02),0px_0px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.08)]'
    }
})
export class AppConfigurator {
    router = inject(Router);
    layoutService = inject(LayoutService);
    themeApplyService = inject(ThemeApplyService);
    themePreferenceService = inject(ThemePreferenceService);

    presets = [...THEME_PRESET_NAMES];
    surfaces = THEME_SURFACES;

    showMenuModeButton = signal(!this.router.url.includes('auth'));

    menuModeOptions = [
        { label: 'Static', value: 'static' },
        { label: 'Overlay', value: 'overlay' }
    ];

    selectedPrimaryColor = computed(() => this.layoutService.layoutConfig().primary);
    selectedSurfaceColor = computed(() => this.layoutService.layoutConfig().surface);
    selectedPreset = computed(() => this.layoutService.layoutConfig().preset);
    menuMode = computed(() => this.layoutService.layoutConfig().menuMode);

    primaryColors = computed(() => this.themeApplyService.getPrimaryColors(this.layoutService.layoutConfig().preset ?? 'Aura'));

    updateColors(event: Event, type: string, colorName: string) {
        const nextConfig = this.getCurrentThemeConfig();

        if (type === 'primary') {
            nextConfig.primary = colorName;
            this.layoutService.layoutConfig.update((state) => ({ ...state, primary: colorName }));
            this.themeApplyService.applyPrimaryChange(nextConfig);
        } else if (type === 'surface') {
            nextConfig.surface = colorName;
            this.layoutService.layoutConfig.update((state) => ({ ...state, surface: colorName }));
            this.themeApplyService.applySurfaceChange(colorName);
        }

        event.stopPropagation();
    }

    onPresetChange(preset: string) {
        this.layoutService.layoutConfig.update((state) => ({ ...state, preset }));
        this.themeApplyService.applyFromConfig(this.getCurrentThemeConfig());
    }

    onMenuModeChange(event: string) {
        this.layoutService.layoutConfig.update((prev) => ({ ...prev, menuMode: event }));
    }

    resetTheme() {
        this.themePreferenceService.resetToDefaults();
    }

    private getCurrentThemeConfig(): IThemeSettings {
        const config = this.layoutService.layoutConfig();
        return {
            preset: config.preset ?? 'Aura',
            primary: config.primary ?? 'lime',
            surface: config.surface ?? 'viva',
            darkTheme: !!config.darkTheme,
            menuMode: config.menuMode ?? 'static'
        };
    }
}
